import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import BigContent from "veneer/BigContent";
import Box from "veneer/Box";
import Chart, { ChartData, ChartOptions } from "veneer/Chart";
import DataTable, { createColumnHelper, sorting } from "veneer/DataTable";

import { useAppSelector } from "cortex/ui/src/hooks";
import {
  accountEventsSelector,
  incidentsSelector,
  strategyStatsSelector,
  uptimesSelector,
} from "cortex/ui/src/redux-store/ui-state/account-stats/selectors";
import { WidgetContainer } from "cortex/ui/src/ui/dashboard";
import { formatDateTime } from "cortex/ui/src/ui/table/formats";
import type { Incident } from "cortex/ui/src/zeus/view/incidents/model";

interface StrategyStat {
  count: number;
  id: number | null;
  name: string;
}

interface UptimeRecord {
  report_date: string;
  uptime: number;
}

type UptimeSeries = ReadonlyArray<UptimeRecord | number>;
type UptimesByNetwork = Record<string, UptimeSeries>;

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const strategyColumn = createColumnHelper<StrategyStat>();
const incidentColumn = createColumnHelper<Incident>();

const strategyColumns = [
  strategyColumn.accessor("name", {
    header: "Strategy",
    cell: (prop) =>
      prop.getValue() === "no_strategy" ? "No strategy" : prop.getValue(),
  }),
  strategyColumn.accessor("count", {
    header: "Devices",
    meta: { align: "right" },
    sortingFn: sorting.number((row) => row.count),
  }),
];

const incidentColumns = [
  incidentColumn.accessor("name", { header: "Alert" }),
  incidentColumn.accessor("device_name", { header: "Device" }),
  incidentColumn.accessor("create_date", {
    header: "Created",
    cell: (prop) => formatDateTime(prop.getValue()),
  }),
  incidentColumn.accessor("resolved", {
    header: "Status",
    cell: (prop) => (prop.getValue() ? "Resolved" : "Open"),
  }),
];

function getUptimeChartData(uptimes: UptimesByNetwork): ChartData<"line"> {
  return {
    datasets: Object.entries(uptimes).map(([networkName, series]) => ({
      data: series
        .map((record, index) => ({
          x:
            typeof record === "number"
              ? index * DAY_IN_MS
              : new Date(record.report_date).getTime(),
          y: typeof record === "number" ? record : record.uptime,
        }))
        .sort((a, b) => a.x - b.x),
      label: networkName,
    })),
  };
}

const uptimeChartOptions: ChartOptions<"line"> = {
  animation: false,
  maintainAspectRatio: false,
  parsing: false,
  plugins: {
    legend: { align: "start", position: "bottom" },
  },
  scales: {
    x: { type: "linear" },
    y: { max: 100, min: 0 },
  },
};

export function EventsWidget() {
  const eventCount = useAppSelector(accountEventsSelector) as number | null;

  return (
    <WidgetContainer dataTestId="events-widget">
      <Box
        alignItems="center"
        display="flex"
        flex={1}
        justifyContent="center"
        p={3}
      >
        <BigContent label="Events">
          {eventCount?.toLocaleString() ?? "—"}
        </BigContent>
      </Box>
    </WidgetContainer>
  );
}

export function NetworkUptimeWidget() {
  const uptimeStats = useAppSelector(uptimesSelector) as UptimesByNetwork;
  const uptimes = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(uptimeStats).filter(([, series]) => series.length > 0),
      ),
    [uptimeStats],
  );
  const chartData = useMemo(() => getUptimeChartData(uptimes), [uptimes]);
  const hasData = Object.keys(uptimes).length > 0;

  return (
    <WidgetContainer dataTestId="network-uptime-widget" title="Network Uptime">
      {hasData ? (
        <Chart
          data={chartData}
          height={320}
          options={uptimeChartOptions}
          showLegend
          type="line"
        />
      ) : (
        <Box p={3} textAlign="center">
          No uptime data available.
        </Box>
      )}
    </WidgetContainer>
  );
}

export function RecentIncidentsWidget() {
  const incidents = useAppSelector(incidentsSelector);
  const recentIncidents = useMemo(
    () =>
      [...incidents].sort(
        (a, b) =>
          new Date(b.create_date).getTime() - new Date(a.create_date).getTime(),
      ),
    [incidents],
  );

  return (
    <WidgetContainer
      dataTestId="recent-incidents-widget"
      title="Recent Incidents"
    >
      <DataTable
        columns={incidentColumns}
        emptyState="No incidents in the last 24 hours."
        initialPageSize={5}
        rows={recentIncidents}
      />
      <Box p={1} textAlign="right">
        <Link to="/incidents">View all incidents</Link>
      </Box>
    </WidgetContainer>
  );
}

export function StrategyDistributionWidget() {
  const strategies = useAppSelector(
    strategyStatsSelector,
  ) as ReadonlyArray<StrategyStat>;

  return (
    <WidgetContainer
      dataTestId="strategy-distribution-widget"
      title="Strategy Distribution"
    >
      <DataTable
        columns={strategyColumns}
        emptyState="No strategy data available."
        initialPageSize={10}
        initialSort={{ id: "count", desc: true }}
        rows={strategies}
      />
    </WidgetContainer>
  );
}
